/* ==========================================================================
   cbg-cart.js — interactive components for the cart page reskin.
   Built on Dawn globals already present on the page: routes, fetchConfig,
   PUB_SUB_EVENTS, publish. Dawn's cart.js remains the update engine — these
   components feed it via the same cartUpdate pub/sub and the line inputs.

   Components:
     <cbg-cart-edit>  — one shared quick-view/edit modal (quantity only)
     <cbg-quick-add>  — predictive-search add-to-cart box
     <cbg-cart-promo> — on-cart discount code (Cart Ajax update.js)
   ========================================================================== */
(function () {
  'use strict';

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  function addUrl() {
    return (window.routes && window.routes.cart_add_url) || root + 'cart/add.js';
  }
  function updateUrl() {
    return (window.routes && window.routes.cart_update_url) || root + 'cart/update.js';
  }
  function predictiveUrl() {
    return (window.routes && window.routes.predictive_search_url) || root + 'search/suggest';
  }

  // Read a product's custom.min_order_quantity metafield client-side. The
  // /products/{handle}.js endpoint omits metafields, so we render the tiny
  // cbg-min-qty section for this product via the Section Rendering API and
  // pull the value out of its data attribute. Falls back to 1 on any error.
  function fetchMinQty(handle) {
    return fetch(root + 'products/' + handle + '?section_id=cbg-min-qty')
      .then(function (r) {
        return r.ok ? r.text() : '';
      })
      .then(function (html) {
        var m = html.match(/data-cbg-min-qty="(\d+)"/);
        var n = m ? parseInt(m[1], 10) : 1;
        return n > 1 ? n : 1;
      })
      .catch(function () {
        return 1;
      });
  }

  // Publish a cart update so Dawn's cart.js re-renders the list + summary.
  // NOTE: PUB_SUB_EVENTS is a bare `const` in constants.js (NOT on window),
  // so it must be referenced as a global identifier, not window.PUB_SUB_EVENTS.
  function notifyCartUpdate(source, cartData) {
    try {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: source, cartData: cartData });
      }
    } catch (e) {
      /* pub/sub not ready — cart still updated server-side */
    }
  }

  // HTML-escape helpers for the client-built Saved-For-Later cards.
  function esc(s) {
    return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }
  function attr(s) {
    return esc(s).replace(/"/g, '&quot;');
  }
  function cssEscape(s) {
    return String(s == null ? '' : s).replace(/["\\]/g, '\\$&');
  }

  // Format an amount in cents to the store currency (predictive search returns
  // product price in cents). Falls back to a plain "$" if Intl is unavailable.
  function money(cents) {
    var cur = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: cur }).format((cents || 0) / 100);
    } catch (e) {
      return '$' + ((cents || 0) / 100).toFixed(2);
    }
  }

  /* ---- Edit modal ------------------------------------------------------ */
  if (!customElements.get('cbg-cart-edit')) {
    customElements.define(
      'cbg-cart-edit',
      class CbgCartEdit extends HTMLElement {
        connectedCallback() {
          this.dialog = this.querySelector('[data-cbg-modal]');
          this.imageEl = this.querySelector('[data-cbg-modal-image]');
          this.titleEl = this.querySelector('[data-cbg-modal-title]');
          this.detailsLink = this.querySelector('[data-cbg-modal-details]');
          this.priceEl = this.querySelector('[data-cbg-modal-price]');
          this.skuEl = this.querySelector('[data-cbg-modal-sku]');
          this.qtyInput = this.querySelector('[data-cbg-modal-qty]');
          this.updateBtn = this.querySelector('[data-cbg-modal-update]');
          this.currentLine = null;
          this.lastFocus = null;

          // Delegated: Edit triggers live inside cart-items and are re-rendered.
          this.onDocClick = this.onDocClick.bind(this);
          this.onRootClick = this.onRootClick.bind(this);
          this.onKeydown = this.onKeydown.bind(this);
          this.close = this.close.bind(this);
          this.onUpdate = this.onUpdate.bind(this);

          document.addEventListener('click', this.onDocClick);
          document.addEventListener('keydown', this.onKeydown);
          // Click on the dim wrapper (this element itself, outside the window) closes.
          this.addEventListener('click', this.onRootClick);
          this.querySelectorAll('[data-cbg-modal-close]').forEach((b) => b.addEventListener('click', this.close));
          if (this.updateBtn) this.updateBtn.addEventListener('click', this.onUpdate);
        }

        disconnectedCallback() {
          document.removeEventListener('click', this.onDocClick);
          document.removeEventListener('keydown', this.onKeydown);
          this.removeEventListener('click', this.onRootClick);
        }

        onDocClick(event) {
          var trigger = event.target.closest('[data-cbg-cart-edit]');
          if (!trigger) return;
          event.preventDefault();
          this.open(trigger);
        }

        onRootClick(event) {
          // Only the wrapper itself (the dim), never a click inside the window.
          if (event.target === this) this.close();
        }

        open(trigger) {
          this.currentLine = trigger.getAttribute('data-line-index');
          this.lastFocus = trigger;
          if (this.imageEl) {
            var img = trigger.getAttribute('data-image');
            if (img) {
              this.imageEl.src = img;
              this.imageEl.style.display = '';
            } else {
              this.imageEl.style.display = 'none';
            }
          }
          if (this.titleEl) this.titleEl.textContent = trigger.getAttribute('data-title') || '';
          if (this.detailsLink) this.detailsLink.href = trigger.getAttribute('data-url') || '#';
          if (this.priceEl) this.priceEl.textContent = trigger.getAttribute('data-price') || '';
          if (this.skuEl) {
            var sku = trigger.getAttribute('data-sku');
            this.skuEl.textContent = sku ? this.skuEl.getAttribute('data-prefix') + ' ' + sku : '';
          }
          if (this.qtyInput) this.qtyInput.value = trigger.getAttribute('data-qty') || '1';

          this.hidden = false;
          document.documentElement.classList.add('cbg-cart-modal-open');
          if (this.qtyInput) this.qtyInput.focus();
        }

        close() {
          this.hidden = true;
          document.documentElement.classList.remove('cbg-cart-modal-open');
          if (this.lastFocus && document.contains(this.lastFocus)) this.lastFocus.focus();
        }

        onKeydown(event) {
          if (event.key === 'Escape' && !this.hidden) this.close();
        }

        onUpdate() {
          if (this.currentLine == null) return;
          var lineInput = document.getElementById('Quantity-' + this.currentLine);
          if (lineInput) {
            lineInput.value = parseInt(this.qtyInput.value, 10) || 1;
            // Hand off to Dawn's cart.js exactly like a manual quantity change.
            lineInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
          this.close();
        }
      }
    );
  }

  /* ---- Quick Add ------------------------------------------------------- */
  if (!customElements.get('cbg-quick-add')) {
    customElements.define(
      'cbg-quick-add',
      class CbgQuickAdd extends HTMLElement {
        connectedCallback() {
          this.toggle = this.querySelector('[data-cbg-qa-toggle]');
          this.panel = this.querySelector('[data-cbg-qa-panel]');
          this.input = this.querySelector('[data-cbg-qa-input]');
          this.results = this.querySelector('[data-cbg-qa-results]');
          this.qtyInput = this.querySelector('[data-cbg-qa-qty]');
          this.addBtn = this.querySelector('[data-cbg-qa-add]');
          this.errorEl = this.querySelector('[data-cbg-qa-error]');
          this.itemLabel = (this.results && this.results.getAttribute('data-item-label')) || '';
          this.selectedId = null;
          this.minQty = 1;
          this.controller = null;
          this.debounce = null;

          this.onToggle = this.onToggle.bind(this);
          this.onInput = this.onInput.bind(this);
          this.onResultsClick = this.onResultsClick.bind(this);
          this.onQtyStep = this.onQtyStep.bind(this);
          this.onAdd = this.onAdd.bind(this);

          if (this.toggle) this.toggle.addEventListener('click', this.onToggle);
          if (this.input) this.input.addEventListener('input', this.onInput);
          if (this.results) this.results.addEventListener('click', this.onResultsClick);
          this.querySelectorAll('[data-cbg-qa-step]').forEach((b) => b.addEventListener('click', this.onQtyStep));
          if (this.addBtn) this.addBtn.addEventListener('click', this.onAdd);

          // This box is rendered INSIDE <cart-items>, whose delegated `change`
          // listener (Dawn's cart.js) treats any bubbling change as a line-item
          // quantity edit — it then fires the native "increments of" validation
          // popup and resets our field toward its value="1" attribute. Our own
          // inputs are not cart lines, so stop their change events here.
          this.onChangeStop = (e) => e.stopPropagation();
          this.addEventListener('change', this.onChangeStop);
        }

        disconnectedCallback() {
          if (this.controller) this.controller.abort();
          if (this.debounce) clearTimeout(this.debounce);
          if (this.onChangeStop) this.removeEventListener('change', this.onChangeStop);
        }

        onToggle() {
          var expanded = this.toggle.getAttribute('aria-expanded') === 'true';
          this.toggle.setAttribute('aria-expanded', String(!expanded));
          if (this.panel) this.panel.hidden = expanded;
        }

        onInput() {
          this.selectedId = null;
          this.resetMin();
          var q = this.input.value.trim();
          if (this.debounce) clearTimeout(this.debounce);
          if (q.length < 2) {
            this.clearResults();
            return;
          }
          this.debounce = setTimeout(() => this.search(q), 250);
        }

        search(q) {
          if (this.controller) this.controller.abort();
          this.controller = new AbortController();
          var url =
            predictiveUrl() +
            '.json?q=' +
            encodeURIComponent(q) +
            '&resources[type]=product&resources[limit]=8' +
            '&resources[options][fields]=title,vendor,product_type,variants.title,variants.sku';
          fetch(url, { signal: this.controller.signal })
            .then((r) => r.json())
            .then((data) => {
              var products = (data.resources && data.resources.results && data.resources.results.products) || [];
              this.renderResults(products);
            })
            .catch(() => {
              /* no-match / abort — show nothing, per spec */
            });
        }

        renderResults(products) {
          if (!this.results) return;
          if (!products.length) {
            this.clearResults();
            return;
          }
          var itemLabel = this.itemLabel;
          this.results.innerHTML = products
            .map(function (p) {
              var img = (p.featured_image && p.featured_image.url) || p.image || '';
              var variant = (p.variants && p.variants[0]) || {};
              var sku = variant.sku || '';
              // Predictive search returns price in cents; format defensively.
              var priceHtml = '';
              if (typeof p.price === 'number') {
                priceHtml = '<span class="cbg-qa__result-price">' + money(p.price) + '</span>';
              } else if (typeof p.price === 'string' && p.price) {
                priceHtml = '<span class="cbg-qa__result-price">' + esc(p.price) + '</span>';
              }
              var media = img
                ? '<img class="cbg-qa__result-img" src="' + attr(img) + '" alt="" width="56" height="56" loading="lazy">'
                : '<span class="cbg-qa__result-img cbg-qa__result-img--empty" aria-hidden="true"></span>';
              return (
                '<li><button type="button" class="cbg-qa__result" data-handle="' +
                attr(p.handle || '') +
                '">' +
                media +
                '<span class="cbg-qa__result-info">' +
                '<span class="cbg-qa__result-title">' +
                esc(p.title || '') +
                '</span>' +
                (sku ? '<span class="cbg-qa__result-sku">' + esc(itemLabel) + ' ' + esc(sku) + '</span>' : '') +
                priceHtml +
                '</span></button></li>'
              );
            })
            .join('');
          this.results.hidden = false;
        }

        clearResults() {
          if (this.results) {
            this.results.innerHTML = '';
            this.results.hidden = true;
          }
        }

        onResultsClick(event) {
          var btn = event.target.closest('.cbg-qa__result');
          if (!btn) return;
          var handle = btn.getAttribute('data-handle');
          var q = this.input.value.trim().toLowerCase();
          // Resolve the exact variant (match SKU if the query is one, else first
          // available) AND read the product's minimum order quantity in parallel.
          Promise.all([
            fetch(root + 'products/' + handle + '.js').then((r) => r.json()),
            fetchMinQty(handle),
          ])
            .then((res) => {
              var product = res[0];
              var minQty = res[1];
              var variants = product.variants || [];
              var match = variants.filter(function (v) {
                return v.sku && v.sku.toLowerCase() === q;
              })[0];
              var available = variants.filter(function (v) {
                return v.available;
              })[0];
              var chosen = match || available || variants[0];
              if (!chosen) return;
              this.selectedId = chosen.id;
              this.input.value = product.title;
              this.applyMin(minQty);
              this.clearResults();
            })
            .catch(() => {});
        }

        // Set the minimum floor + prefill the quantity field to it, so the box
        // starts at a valid amount (no under-min line, no input warning).
        applyMin(min) {
          this.minQty = min > 1 ? min : 1;
          if (this.qtyInput) {
            this.qtyInput.min = this.minQty;
            this.qtyInput.value = this.minQty;
          }
        }

        resetMin() {
          this.minQty = 1;
          if (this.qtyInput) this.qtyInput.min = 1;
        }

        onQtyStep(event) {
          if (!this.qtyInput) return;
          var floor = this.minQty || 1;
          var dir = event.currentTarget.getAttribute('data-cbg-qa-step');
          var val = parseInt(this.qtyInput.value, 10) || floor;
          val = dir === 'minus' ? Math.max(floor, val - 1) : val + 1;
          this.qtyInput.value = val;
        }

        onAdd() {
          if (!this.selectedId) return;
          var self = this;
          var floor = this.minQty || 1;
          var qty = parseInt(this.qtyInput && this.qtyInput.value, 10);
          if (!qty || qty < floor) qty = floor;
          this.showError('');
          this.addBtn.setAttribute('disabled', 'disabled');
          fetch(addUrl(), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
            body: JSON.stringify({ items: [{ id: this.selectedId, quantity: qty }] }),
          })
            .then(function (r) {
              return r.json().then(function (data) {
                return { ok: r.ok, data: data };
              });
            })
            .then(function (res) {
              if (!res.ok) {
                // Shopify rejected the add (inventory, quantity rule, etc.).
                self.showError((res.data && (res.data.description || res.data.message)) || '');
                return;
              }
              // First item into an empty cart: cart.js's cartUpdate handler
              // re-renders the list HTML but never clears .is-empty, so the new
              // line would stay hidden. Clear it here so the list shows at once.
              var cartItems = self.closest('cart-items');
              if (cartItems) cartItems.classList.remove('is-empty');
              self.reset();
              // Dawn's cart.js re-fetches + swaps the cart list/summary/icon.
              notifyCartUpdate('cbg-quick-add');
            })
            .catch(function () {
              self.showError('');
            })
            .finally(function () {
              if (self.addBtn) self.addBtn.removeAttribute('disabled');
            });
        }

        // Clear the box back to its empty state after a successful add.
        reset() {
          this.selectedId = null;
          this.resetMin();
          if (this.input) this.input.value = '';
          if (this.qtyInput) this.qtyInput.value = 1;
          this.clearResults();
        }

        showError(msg) {
          if (!this.errorEl) return;
          if (msg) {
            this.errorEl.textContent = msg;
            this.errorEl.hidden = false;
          } else {
            this.errorEl.textContent = '';
            this.errorEl.hidden = true;
          }
        }
      }
    );
  }

  /* ---- Promo code ------------------------------------------------------ */
  if (!customElements.get('cbg-cart-promo')) {
    customElements.define(
      'cbg-cart-promo',
      class CbgCartPromo extends HTMLElement {
        connectedCallback() {
          this.toggle = this.querySelector('[data-cbg-promo-toggle]');
          this.panel = this.querySelector('[data-cbg-promo-panel]');
          this.form = this.querySelector('[data-cbg-promo-form]');
          this.input = this.querySelector('[data-cbg-promo-input]');
          this.error = this.querySelector('[data-cbg-promo-error]');

          this.onToggle = this.onToggle.bind(this);
          this.onSubmit = this.onSubmit.bind(this);
          if (this.toggle) this.toggle.addEventListener('click', this.onToggle);
          if (this.form) this.form.addEventListener('submit', this.onSubmit);
        }

        onToggle() {
          var expanded = this.toggle.getAttribute('aria-expanded') === 'true';
          this.toggle.setAttribute('aria-expanded', String(!expanded));
          if (this.panel) this.panel.hidden = expanded;
        }

        existingCodes() {
          return Array.prototype.map.call(
            document.querySelectorAll('[data-cbg-applied-code]'),
            function (el) {
              return el.getAttribute('data-cbg-applied-code');
            }
          );
        }

        onSubmit(event) {
          event.preventDefault();
          var code = (this.input.value || '').trim();
          if (!code) return;
          if (this.error) this.error.hidden = true;

          var codes = this.existingCodes();
          if (codes.indexOf(code) === -1) codes.push(code);

          fetch(updateUrl(), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ discount: codes.join(',') }),
          })
            .then((r) => r.json())
            .then((cart) => {
              var list = cart.discount_codes || [];
              var rejected = list.filter(function (d) {
                return d.code.toLowerCase() === code.toLowerCase() && d.applicable === false;
              })[0];
              if (rejected) {
                if (this.error) this.error.hidden = false;
                this.input.value = '';
                return;
              }
              this.input.value = '';
              notifyCartUpdate('cbg-cart-promo', cart);
            })
            .catch(() => {
              if (this.error) this.error.hidden = false;
            });
        }
      }
    );
  }

  /* ---- Live "N Products, M Items" sub-line ----------------------------- */
  // Sits directly under the cart title (outside .js-contents, so Dawn's granular
  // quantity re-render doesn't touch it) — kept in sync from the cartUpdate payload.
  if (!customElements.get('cbg-cart-count')) {
    customElements.define(
      'cbg-cart-count',
      class CbgCartCount extends HTMLElement {
        connectedCallback() {
          this.productsWord = this.getAttribute('data-products') || '';
          this.itemsWord = this.getAttribute('data-items') || '';
          try {
            if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              this.unsub = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
                if (event && event.cartData) this.render(event.cartData);
              });
            }
          } catch (e) {
            /* pub/sub not ready */
          }
        }

        disconnectedCallback() {
          if (this.unsub) this.unsub();
        }

        render(cart) {
          var lines = (cart.items || []).length;
          var count = cart.item_count || 0;
          this.textContent = lines + ' ' + this.productsWord + ', ' + count + ' ' + this.itemsWord;
        }
      }
    );
  }

  /* ---- Saved For Later -------------------------------------------------- */
  // A per-customer localStorage list. Clicking a line's "Save for Later" snapshots
  // the line and removes it from the cart (via Dawn's quantity engine). Guests are
  // redirected to login. Renders an inline row (desktop) and an off-canvas pusher
  // (mobile), both driven from the same localStorage list.
  if (!customElements.get('cbg-saved-for-later')) {
    customElements.define(
      'cbg-saved-for-later',
      class CbgSavedForLater extends HTMLElement {
        connectedCallback() {
          this.loggedIn = this.getAttribute('data-logged-in') === 'true';
          this.customerId = this.getAttribute('data-customer-id') || '';
          this.loginUrl = this.getAttribute('data-login-url') || root + 'account/login';
          this.wordProduct = this.getAttribute('data-word-product') || 'Product';
          this.wordProducts = this.getAttribute('data-word-products') || 'Products';
          this.countEmpty = this.getAttribute('data-count-empty') || '';
          this.labelMove = this.getAttribute('data-label-move') || 'Move to Cart';
          this.labelRemove = this.getAttribute('data-label-remove') || 'Remove';
          this.labelQty = this.getAttribute('data-label-qty') || 'Quantity:';
          this.decreaseLabel = this.getAttribute('data-decrease') || '';
          this.increaseLabel = this.getAttribute('data-increase') || '';

          this.toast = this.querySelector('[data-cbg-saved-toast]');
          this.pusher = this.querySelector('[data-cbg-saved-pusher]');
          this.openBtn = this.querySelector('[data-cbg-saved-open]');

          this.onDocClick = this.onDocClick.bind(this);
          this.onClick = this.onClick.bind(this);
          this.onQtyChange = this.onQtyChange.bind(this);
          this.onKeydown = this.onKeydown.bind(this);

          document.addEventListener('click', this.onDocClick);
          this.addEventListener('click', this.onClick);
          this.addEventListener('change', this.onQtyChange);
          document.addEventListener('keydown', this.onKeydown);

          this.hidden = false;
          this.render();
        }

        disconnectedCallback() {
          document.removeEventListener('click', this.onDocClick);
          document.removeEventListener('keydown', this.onKeydown);
          if (this.toastTimer) clearTimeout(this.toastTimer);
        }

        /* ---- storage ---- */
        storageKey() {
          return 'cbg_saved_for_later_' + (this.customerId || 'guest');
        }
        load() {
          try {
            return JSON.parse(localStorage.getItem(this.storageKey())) || [];
          } catch (e) {
            return [];
          }
        }
        persist(list) {
          try {
            localStorage.setItem(this.storageKey(), JSON.stringify(list));
          } catch (e) {
            /* storage full / unavailable — nothing else we can do */
          }
        }

        /* ---- Save for Later (delegated: buttons live in re-rendered .js-contents) ---- */
        onDocClick(event) {
          var trigger = event.target.closest('[data-cbg-save-for-later]');
          if (!trigger) return;
          event.preventDefault();
          if (!this.loggedIn) {
            window.location.href = this.loginUrl;
            return;
          }
          this.saveItem(trigger);
        }

        saveItem(trigger) {
          var variantId = trigger.getAttribute('data-variant-id');
          if (!variantId) return;
          var entry = {
            variantId: variantId,
            handle: trigger.getAttribute('data-handle') || '',
            url: trigger.getAttribute('data-url') || '',
            title: trigger.getAttribute('data-title') || '',
            image: trigger.getAttribute('data-image') || '',
            price: trigger.getAttribute('data-price') || '',
            qty: parseInt(trigger.getAttribute('data-quantity'), 10) || 1,
          };
          var list = this.load().filter(function (e) {
            return String(e.variantId) !== String(variantId);
          });
          list.push(entry);
          this.persist(list);

          this.removeFromCart(trigger);
          this.render();
          this.showToast();
        }

        // Reuse Dawn's tested removal path: set the line's quantity to 0 and let
        // cart.js update the cart + subtotal + live region (source 'cart-items',
        // so Dawn does NOT full-rerender cart-items and this saved section stays put).
        // Fully remove the line — mirror Dawn's own "Remove" link. We CANNOT do
        // this by writing 0 into the quantity input: Dawn's onChange runs
        // validateQuantity, sees 0 < data-min (min is 1) and rejects it, snapping
        // the value back to the original qty. Dawn's cart-remove-button bypasses
        // that validation by calling updateQuantity(index, 0) directly, so we
        // trigger it — same code path as a real "Remove" click (removal, subtotal,
        // is-empty toggle, cart-icon-bubble refresh, live region — all handled).
        removeFromCart(trigger) {
          var line = trigger.closest('.cart-item');
          var removeBtn = line && line.querySelector('cart-remove-button');
          if (removeBtn) {
            removeBtn.click();
            return;
          }
          // Fallback: remove by line key via Cart Ajax, then ask Dawn to re-render.
          var key = trigger.getAttribute('data-line-key');
          if (!key) return;
          var updates = {};
          updates[key] = 0;
          fetch(updateUrl(), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ updates: updates }),
          })
            .then(function (r) {
              return r.json();
            })
            .then((cart) => notifyCartUpdate('cbg-saved-for-later', cart))
            .catch(function () {});
        }

        /* ---- component-internal clicks ---- */
        onClick(event) {
          var t = event.target;
          if (t.closest('[data-cbg-saved-toast-close]')) return this.hideToast();
          if (t.closest('[data-cbg-saved-open]')) return this.openPusher();
          if (t.closest('[data-cbg-saved-close]')) return this.closePusher();
          var step = t.closest('[data-cbg-saved-step]');
          if (step) return this.step(step);
          var move = t.closest('[data-cbg-saved-move]');
          if (move) return this.moveToCart(move);
          var remove = t.closest('[data-cbg-saved-remove]');
          if (remove) return this.removeSaved(remove);
        }

        step(btn) {
          var card = btn.closest('[data-cbg-saved-card]');
          var input = card && card.querySelector('[data-cbg-saved-qty-input]');
          if (!input) return;
          var val = parseInt(input.value, 10) || 1;
          val = btn.getAttribute('data-cbg-saved-step') === 'minus' ? Math.max(1, val - 1) : val + 1;
          this.updateQty(card.getAttribute('data-variant-id'), val);
        }

        onQtyChange(event) {
          var input = event.target.closest('[data-cbg-saved-qty-input]');
          if (!input) return;
          var card = input.closest('[data-cbg-saved-card]');
          this.updateQty(card.getAttribute('data-variant-id'), Math.max(1, parseInt(input.value, 10) || 1));
        }

        updateQty(variantId, qty) {
          var list = this.load().map(function (e) {
            if (String(e.variantId) === String(variantId)) e.qty = qty;
            return e;
          });
          this.persist(list);
          // Keep desktop + pusher copies of the same card in sync.
          this.querySelectorAll(
            '[data-cbg-saved-card][data-variant-id="' + cssEscape(variantId) + '"] [data-cbg-saved-qty-input]'
          ).forEach(function (i) {
            i.value = qty;
          });
        }

        moveToCart(btn) {
          var card = btn.closest('[data-cbg-saved-card]');
          if (!card) return;
          var variantId = card.getAttribute('data-variant-id');
          var input = card.querySelector('[data-cbg-saved-qty-input]');
          var qty = Math.max(1, parseInt(input && input.value, 10) || 1);
          btn.setAttribute('disabled', 'disabled');
          fetch(addUrl(), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
            body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: qty }] }),
          })
            .then(function (r) {
              return r.json();
            })
            .then((res) => {
              if (res && res.status && res.status >= 400) {
                btn.removeAttribute('disabled');
                return;
              }
              var list = this.load().filter(function (e) {
                return String(e.variantId) !== String(variantId);
              });
              this.persist(list);
              // Cart is now non-empty — clear the empty-state class before Dawn's
              // onCartUpdate swaps in the (populated) server HTML.
              var cartItems = this.closest('cart-items');
              if (cartItems) cartItems.classList.remove('is-empty');
              var footer = document.getElementById('main-cart-footer');
              if (footer) footer.classList.remove('is-empty');
              notifyCartUpdate('cbg-saved-for-later');
              this.render();
            })
            .catch(function () {
              btn.removeAttribute('disabled');
            });
        }

        removeSaved(btn) {
          var card = btn.closest('[data-cbg-saved-card]');
          if (!card) return;
          var variantId = card.getAttribute('data-variant-id');
          var list = this.load().filter(function (e) {
            return String(e.variantId) !== String(variantId);
          });
          this.persist(list);
          this.render();
        }

        /* ---- render ---- */
        render() {
          var list = this.load();
          var count = list.length;
          var self = this;

          this.querySelectorAll('[data-cbg-saved-count]').forEach(function (el) {
            el.textContent =
              count === 0 ? self.countEmpty : count + ' ' + (count === 1 ? self.wordProduct : self.wordProducts);
          });
          // The empty / populated sub-text is rendered as plain Liquid text and
          // toggled by the .cbg-cart__saved--empty class below (see render tail).

          var html = list
            .map(function (e) {
              return self.cardHtml(e);
            })
            .join('');
          this.querySelectorAll('[data-cbg-saved-list]').forEach(function (el) {
            el.innerHTML = html;
          });

          this.classList.toggle('cbg-cart__saved--empty', count === 0);
        }

        cardHtml(e) {
          var thumb = e.image
            ? '<a class="cbg-cart__saved-thumb" href="' +
              attr(e.url) +
              '" tabindex="-1" aria-hidden="true"><img src="' +
              attr(e.image) +
              '" alt="' +
              attr(e.title) +
              '" width="150" height="150" loading="lazy"></a>'
            : '';
          return (
            '<div class="cbg-cart__saved-card" data-cbg-saved-card data-variant-id="' +
            attr(e.variantId) +
            '">' +
            thumb +
            '<a class="cbg-cart__saved-name" href="' +
            attr(e.url) +
            '">' +
            esc(e.title) +
            '</a>' +
            '<div class="cbg-cart__saved-price">' +
            esc(e.price) +
            '</div>' +
            '<div class="cbg-cart__saved-qty">' +
            '<label class="cbg-cart__saved-qty-label">' +
            esc(this.labelQty) +
            '</label>' +
            '<div class="cbg-cart__saved-stepper">' +
            '<button type="button" class="cbg-cart__saved-step" data-cbg-saved-step="minus" aria-label="' +
            attr(this.decreaseLabel) +
            '">&minus;</button>' +
            '<input type="number" class="cbg-cart__saved-qty-input" data-cbg-saved-qty-input value="' +
            attr(e.qty) +
            '" min="1" inputmode="numeric">' +
            '<button type="button" class="cbg-cart__saved-step" data-cbg-saved-step="plus" aria-label="' +
            attr(this.increaseLabel) +
            '">&plus;</button>' +
            '</div>' +
            '</div>' +
            '<button type="button" class="cbg-cart__saved-move" data-cbg-saved-move>' +
            esc(this.labelMove) +
            '</button>' +
            '<button type="button" class="cbg-cart__saved-remove-btn" data-cbg-saved-remove>' +
            esc(this.labelRemove) +
            '</button>' +
            '</div>'
          );
        }

        /* ---- success toast ---- */
        showToast() {
          if (!this.toast) return;
          this.toast.hidden = false;
          if (this.toastTimer) clearTimeout(this.toastTimer);
          this.toastTimer = setTimeout(() => this.hideToast(), 8000);
        }
        hideToast() {
          if (this.toast) this.toast.hidden = true;
          if (this.toastTimer) clearTimeout(this.toastTimer);
        }

        /* ---- mobile pusher ---- */
        openPusher() {
          if (!this.pusher) return;
          this.pusher.hidden = false;
          void this.pusher.offsetWidth; // reflow so the slide-in transition runs
          this.pusher.classList.add('is-open');
          document.documentElement.classList.add('cbg-saved-pusher-open');
          var back = this.pusher.querySelector('[data-cbg-saved-close]');
          if (back) back.focus();
        }
        closePusher() {
          if (!this.pusher) return;
          this.pusher.classList.remove('is-open');
          document.documentElement.classList.remove('cbg-saved-pusher-open');
          var self = this;
          setTimeout(function () {
            if (!self.pusher.classList.contains('is-open')) self.pusher.hidden = true;
          }, 300);
          if (this.openBtn) this.openBtn.focus();
        }
        onKeydown(event) {
          if (event.key === 'Escape' && this.pusher && this.pusher.classList.contains('is-open')) this.closePusher();
        }
      }
    );
  }
})();
